import { useState, useRef, useCallback, useEffect } from "react";
import type { RecorderStatus } from "@/types";

export type RecordSource = "mic" | "tab+mic" | "tab";

interface UseRecorderReturn {
  status: RecorderStatus;
  durationMs: number;
  formattedDuration: string;
  level: number;
  start: (source?: RecordSource) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<{ blob: Blob; mimeType: string }>;
  error: string | null;
}

export function isTabAudioCaptureSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getDisplayMedia
  );
}

async function buildAudioStream(source: RecordSource): Promise<MediaStream> {
  if (source === "mic") {
    return navigator.mediaDevices.getUserMedia({ audio: true });
  }

  // Tab capture via getDisplayMedia. The browser shows a picker; the user
  // must check "Share tab audio" for this to yield an audio track.
  // Some browsers require the video constraint; we capture but don't keep it.
  const display = await navigator.mediaDevices.getDisplayMedia({
    audio: true,
    video: true,
  });
  display.getVideoTracks().forEach((t) => t.stop());
  display.getVideoTracks().forEach((t) => display.removeTrack(t));

  const tabAudio = display.getAudioTracks();
  if (tabAudio.length === 0) {
    display.getTracks().forEach((t) => t.stop());
    throw new Error(
      "No tab audio track available. Did you enable \"Share tab audio\"?"
    );
  }

  if (source === "tab") return display;

  // tab + mic: mix via Web Audio.
  const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  const ctx = new AudioContext();
  const dest = ctx.createMediaStreamDestination();
  ctx.createMediaStreamSource(display).connect(dest);
  ctx.createMediaStreamSource(mic).connect(dest);

  const mixed = dest.stream;
  // Carry the original tracks alongside so they're cleaned up when we stop.
  display.getTracks().forEach((t) => mixed.addTrack(t));
  mic.getTracks().forEach((t) => mixed.addTrack(t));
  return mixed;
}

function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function getSupportedMimeType(): string {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return "audio/webm";
}

export function useRecorder(): UseRecorderReturn {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [level, setLevel] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const segmentStartRef = useRef(0);
  const accumulatedMsRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      analyserCleanupRef.current?.();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const startLevelMeter = useCallback((stream: MediaStream) => {
    analyserCleanupRef.current?.();
    try {
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      let raf = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sumSq = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / data.length);
        setLevel(Math.min(1, rms * 3));
        raf = requestAnimationFrame(tick);
      };
      tick();
      analyserCleanupRef.current = () => {
        cancelAnimationFrame(raf);
        ctx.close().catch(() => {});
        setLevel(0);
      };
    } catch {
      // VU meter is best-effort — never break the recording over it.
    }
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setDurationMs(accumulatedMsRef.current + (Date.now() - segmentStartRef.current));
    }, 200);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(
    async (source: RecordSource = "mic") => {
      setError(null);
      try {
        const stream = await buildAudioStream(source);
        streamRef.current = stream;

        // Strip non-audio tracks before recording.
        const audioOnly = new MediaStream(stream.getAudioTracks());

        const mimeType = getSupportedMimeType();
        const recorder = new MediaRecorder(audioOnly, { mimeType });
        recorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.start(1000);
        accumulatedMsRef.current = 0;
        segmentStartRef.current = Date.now();
        setStatus("recording");
        setDurationMs(0);
        startTimer();
        startLevelMeter(audioOnly);
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Microphone access denied"
        );
      }
    },
    [startTimer, startLevelMeter]
  );

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    recorder.pause();
    accumulatedMsRef.current += Date.now() - segmentStartRef.current;
    stopTimer();
    setDurationMs(accumulatedMsRef.current);
    setStatus("paused");
  }, [stopTimer]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    recorder.resume();
    segmentStartRef.current = Date.now();
    startTimer();
    setStatus("recording");
  }, [startTimer]);

  const stop = useCallback(async (): Promise<{ blob: Blob; mimeType: string }> => {
    return new Promise((resolve, reject) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        reject(new Error("No active recording"));
        return;
      }

      const wasPaused = recorder.state === "paused";
      setStatus("stopping");
      stopTimer();
      if (!wasPaused) {
        accumulatedMsRef.current += Date.now() - segmentStartRef.current;
      }
      setDurationMs(accumulatedMsRef.current);

      recorder.onstop = () => {
        const mimeType = recorder.mimeType;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        analyserCleanupRef.current?.();
        analyserCleanupRef.current = null;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setStatus("idle");
        resolve({ blob, mimeType });
      };

      recorder.onerror = () => {
        setStatus("idle");
        reject(new Error("Recording failed"));
      };

      recorder.stop();
    });
  }, [stopTimer]);

  return {
    status,
    durationMs,
    formattedDuration: formatTime(durationMs),
    level,
    start,
    pause,
    resume,
    stop,
    error,
  };
}
